import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { api } from '../../../../../../redux/slices/apiSlice'
import {
  useImportOffersMutation,
  useLazyGetOfferImportByIdQuery,
} from '../../../../../../redux/slices/offersSlice'
import {
  hideModal,
  showErrorNotification,
  showModal,
  showSuccessNotification,
} from '../../../../../../redux/actions'
import { OFFER_IMPORT_FILE_SIZE_ERROR } from './offerImportConstants'

export const OFFER_IMPORT_POLL_INTERVAL_MS = 2000
export const OFFER_IMPORT_TIMEOUT_MS = 32000

const IMPORT_STATUS = {
  unspecified: 0,
  pending: 1,
  completed: 2,
  failed: 3,
}

const UPLOAD_VALIDATION_ERRORS = new Set([
  OFFER_IMPORT_FILE_SIZE_ERROR,
  'Only CSV files are supported, please upload CSV file',
])

const IMPORT_VALIDATION_FAILURE_MARKER = 'OFFER_IMPORT_VALIDATION'
const IMPORT_TECHNICAL_FAILURE_MARKER = 'OFFER_IMPORT_TECHNICAL_FAILURE'
const LEGACY_IMPORT_VALIDATION_FAILURE_MARKER = 'Offer import validation failed'
const LEGACY_IMPORT_VALIDATION_FAILURE_PREFIX = `${LEGACY_IMPORT_VALIDATION_FAILURE_MARKER} - `

const isValidTotalOffers = (totalOffers) => (
  Number.isInteger(totalOffers) && totalOffers >= 0
)

const getPartialImportMessage = (totalOffers) => {
  const offerLabel = totalOffers === 1 ? 'offer was' : 'offers were'
  return `${totalOffers} ${offerLabel} imported before the import failed. Do not retry this file unchanged. `
    + 'Export the current offers and include their offer IDs before retrying to avoid duplicates.'
}

const getImportStatus = (status) => {
  if (typeof status === 'string') {
    const normalizedStatus = status.trim().toLowerCase()
    return IMPORT_STATUS[normalizedStatus] ?? Number(normalizedStatus)
  }
  return status
}

const getImportErrors = (errors) => (
  Array.isArray(errors)
    ? errors.filter((error) => typeof error === 'string' && error.trim()).map((error) => error.trim())
    : []
)

const hasMalformedImportErrors = (errors) => errors != null && !Array.isArray(errors)

const getMarkedValidationErrors = (errors) => {
  const importErrors = getImportErrors(errors)
  const [classification, ...details] = importErrors

  if (classification === IMPORT_VALIDATION_FAILURE_MARKER) return details
  if (classification === LEGACY_IMPORT_VALIDATION_FAILURE_MARKER) return details
  if (!classification?.startsWith(LEGACY_IMPORT_VALIDATION_FAILURE_PREFIX)) return null

  const legacyDetail = classification.slice(LEGACY_IMPORT_VALIDATION_FAILURE_PREFIX.length).trim()
  return legacyDetail ? [legacyDetail, ...details] : details
}

const useOfferImport = (
  partnerListingId,
  {
    pollIntervalMs = OFFER_IMPORT_POLL_INTERVAL_MS,
    timeoutMs = OFFER_IMPORT_TIMEOUT_MS,
  } = {},
) => {
  const dispatch = useDispatch()
  const processingRef = useRef(false)
  const activeRunRef = useRef(0)
  const mountedRef = useRef(true)
  const timeoutRef = useRef(null)
  const deadlineTimeoutRef = useRef(null)
  const resolveDelayRef = useRef(null)
  const activeRequestRef = useRef(null)
  const [importOffers] = useImportOffersMutation()
  const [getOfferImport] = useLazyGetOfferImportByIdQuery()

  const isCurrentRun = (runId) => mountedRef.current && activeRunRef.current === runId

  const cancelPendingWork = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (deadlineTimeoutRef.current) {
      clearTimeout(deadlineTimeoutRef.current)
      deadlineTimeoutRef.current = null
    }
    if (resolveDelayRef.current) {
      const resolveDelay = resolveDelayRef.current
      resolveDelayRef.current = null
      resolveDelay(false)
    }
    if (activeRequestRef.current?.abort) {
      activeRequestRef.current.abort()
    }
    activeRequestRef.current = null
  }

  const finishRun = (runId) => {
    if (!isCurrentRun(runId)) return false
    activeRunRef.current += 1
    cancelPendingWork()
    processingRef.current = false
    return true
  }

  const showTechnicalFailure = (runId, totalOffers = 0) => {
    if (!finishRun(runId)) return { closed: true }
    dispatch(hideModal())
    if (totalOffers > 0) {
      dispatch(api.util.invalidateTags(['Offer']))
      dispatch(showErrorNotification(getPartialImportMessage(totalOffers)))
    } else {
      dispatch(showErrorNotification('Something went wrong during offers import. Please try again later.'))
    }
    return { closed: true }
  }

  const showValidationErrors = (runId, errors, totalOffers = 0) => {
    if (!finishRun(runId)) return { closed: true }
    if (totalOffers > 0) {
      dispatch(api.util.invalidateTags(['Offer']))
      return {
        errors: [getPartialImportMessage(totalOffers), ...errors],
      }
    }
    return { errors }
  }

  const showSuccess = (runId, totalOffers) => {
    if (!finishRun(runId)) return { closed: true }
    dispatch(hideModal())
    dispatch(api.util.invalidateTags(['Offer']))
    dispatch(showSuccessNotification(`${totalOffers} offers were imported successfully.`))
    return { closed: true }
  }

  const waitForNextPoll = (runId) => new Promise((resolve) => {
    if (!isCurrentRun(runId)) {
      resolve(false)
      return
    }

    resolveDelayRef.current = resolve
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      resolveDelayRef.current = null
      resolve(isCurrentRun(runId))
    }, pollIntervalMs)
  })

  const pollImport = async (runId, importId) => {
    const shouldPoll = await waitForNextPoll(runId)
    if (!shouldPoll || !isCurrentRun(runId)) return { closed: true }

    let request
    let result
    try {
      request = getOfferImport(importId, false)
      activeRequestRef.current = request
      result = await request.unwrap()
    } catch {
      return showTechnicalFailure(runId)
    } finally {
      if (activeRequestRef.current === request) {
        activeRequestRef.current = null
      }
    }

    if (!isCurrentRun(runId)) return { closed: true }
    if (!result) return showTechnicalFailure(runId)
    const status = getImportStatus(result.status)
    const isTerminalStatus = status === IMPORT_STATUS.completed || status === IMPORT_STATUS.failed
    if (isTerminalStatus && !isValidTotalOffers(result.totalOffers)) {
      return showTechnicalFailure(runId)
    }
    if (hasMalformedImportErrors(result.errors)) {
      return showTechnicalFailure(runId, isTerminalStatus ? result.totalOffers : 0)
    }

    const importErrors = getImportErrors(result.errors)
    if (
      (status === IMPORT_STATUS.unspecified || status === IMPORT_STATUS.pending)
      && importErrors.length === 0
    ) {
      return pollImport(runId, importId)
    }

    if (status === IMPORT_STATUS.completed) {
      if (importErrors.length === 0) {
        return showSuccess(runId, result.totalOffers)
      }

      const markedValidationErrors = getMarkedValidationErrors(importErrors)
      if (markedValidationErrors?.length > 0) {
        return showValidationErrors(runId, markedValidationErrors, result.totalOffers)
      }
      if (
        markedValidationErrors === null
        && importErrors[0] !== IMPORT_TECHNICAL_FAILURE_MARKER
      ) {
        return showValidationErrors(runId, importErrors, result.totalOffers)
      }
      return showTechnicalFailure(runId, result.totalOffers)
    }

    if (status === IMPORT_STATUS.failed) {
      const validationErrors = getMarkedValidationErrors(importErrors)
      if (validationErrors?.length > 0) {
        return showValidationErrors(runId, validationErrors, result.totalOffers)
      }
      return showTechnicalFailure(runId, result.totalOffers)
    }

    return showTechnicalFailure(runId)
  }

  const cancelImport = () => {
    activeRunRef.current += 1
    cancelPendingWork()
    processingRef.current = false
  }

  const onClickImportOffers = () => {
    cancelImport()
    dispatch(showModal('IMPORT_OFFERS_MODAL', {
      onClickClose: () => {
        cancelImport()
        dispatch(hideModal())
      },
      onClickSubmit: async (offersFile) => {
        if (processingRef.current) return { closed: true }

        const runId = activeRunRef.current + 1
        activeRunRef.current = runId
        processingRef.current = true
        deadlineTimeoutRef.current = setTimeout(() => {
          showTechnicalFailure(runId)
        }, timeoutMs)

        let request
        let response
        try {
          request = importOffers({ id: partnerListingId, body: offersFile })
          activeRequestRef.current = request
          response = await request.unwrap()
        } catch {
          return showTechnicalFailure(runId)
        } finally {
          if (activeRequestRef.current === request) {
            activeRequestRef.current = null
          }
        }

        if (!isCurrentRun(runId)) return { closed: true }

        const importId = typeof response?.importId === 'string'
          ? response.importId.trim()
          : ''
        if (response?.success === true && importId) {
          return pollImport(runId, importId)
        }

        if (
          response?.success === false
          && UPLOAD_VALIDATION_ERRORS.has(response.errorMessage)
        ) {
          return showValidationErrors(runId, [response.errorMessage])
        }

        return showTechnicalFailure(runId)
      },
    }))
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      activeRunRef.current += 1
      cancelPendingWork()
    }
  }, [])

  return {
    onClickImportOffers,
  }
}

export default useOfferImport
